import { useState } from "react";
import { Check, ChevronsUpDown, Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { api, formatErrorDetail } from "@/lib/api";
import { toast } from "sonner";

export default function MultiSelectAnlagen({ anlagen, selectedIds, onChange, onAnlagenChanged }) {
  const [open, setOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);

  const toggle = (id) => {
    if (selectedIds.includes(id)) {
      onChange(selectedIds.filter((x) => x !== id));
    } else {
      onChange([...selectedIds, id]);
    }
  };

  const removeOne = (id) => onChange(selectedIds.filter((x) => x !== id));

  const createAnlage = async () => {
    const name = newName.trim();
    if (!name) return;
    setCreating(true);
    try {
      const { data } = await api.post("/anlagen", { name });
      setNewName("");
      onAnlagenChanged?.();
      onChange([...selectedIds, data.id]);
      toast.success(`Anlage „${data.name}" hinzugefügt`);
    } catch (e) {
      toast.error(formatErrorDetail(e.response?.data?.detail) || "Fehler beim Anlegen");
    } finally {
      setCreating(false);
    }
  };

  const selected = anlagen.filter((a) => selectedIds.includes(a.id));

  return (
    <div className="space-y-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between border-slate-300 text-slate-700 font-normal"
            data-testid="anlagen-select-trigger"
          >
            {selected.length > 0
              ? `${selected.length} Anlage${selected.length > 1 ? "n" : ""} ausgewählt`
              : "Anlagen auswählen…"}
            <ChevronsUpDown className="ml-2 h-4 w-4 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
          <div className="max-h-60 overflow-auto py-1">
            {anlagen.length === 0 && (
              <div className="px-3 py-4 text-sm text-slate-500 text-center">
                Noch keine Anlagen vorhanden.
              </div>
            )}
            {anlagen.map((a) => {
              const isSelected = selectedIds.includes(a.id);
              return (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => toggle(a.id)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-slate-50 transition-colors"
                  data-testid={`anlage-option-${a.id}`}
                >
                  <span
                    className={`flex h-4 w-4 items-center justify-center rounded border ${
                      isSelected ? "bg-[#E2001A] border-[#E2001A] text-white" : "border-slate-300"
                    }`}
                  >
                    {isSelected && <Check className="h-3 w-3" />}
                  </span>
                  <span className="truncate">{a.name}</span>
                </button>
              );
            })}
          </div>
          <div className="border-t border-slate-200 p-2 flex gap-2">
            <Input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Neue Anlage anlegen"
              className="h-9"
              data-testid="new-anlage-input"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  createAnlage();
                }
              }}
            />
            <Button
              type="button"
              size="sm"
              onClick={createAnlage}
              disabled={creating || !newName.trim()}
              className="bg-[#E2001A] hover:bg-[#C20015] text-white"
              data-testid="create-anlage-button"
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        </PopoverContent>
      </Popover>

      {selected.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selected.map((a) => (
            <span
              key={a.id}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-700 border border-slate-200"
              data-testid={`selected-anlage-${a.id}`}
            >
              {a.name}
              <button
                type="button"
                onClick={() => removeOne(a.id)}
                className="hover:text-[#E2001A]"
                aria-label={`${a.name} entfernen`}
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
